import { useEffect, useState, useCallback, useRef } from "react";
import api, { errText } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useDrawer } from "@/context/DrawerContext";
import { TierBadge } from "@/components/TierBadge";
import { Pagination } from "@/components/Pagination";
import { toast } from "sonner";
import {
  Linkedin, Copy, Check, Send, Loader2, UserCheck, Trash2, Upload, Users2, Clock, AlertTriangle, Phone, FileUp, AlertCircle, Search, TrendingUp, Crown, Medal, Link2,
} from "lucide-react";

async function copyText(text) {
  try {
    if (navigator.clipboard && window.isSecureContext) { await navigator.clipboard.writeText(text || ""); return true; }
    throw new Error();
  } catch {
    try {
      const ta = document.createElement("textarea");
      ta.value = text || ""; ta.style.position = "fixed"; ta.style.opacity = "0";
      document.body.appendChild(ta); ta.focus(); ta.select(); document.execCommand("copy");
      document.body.removeChild(ta); return true;
    } catch { return false; }
  }
}

const STATUS_STYLE = {
  New: "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Connection Sent": "bg-blue-50 text-klein border-blue-200",
  Converted: "bg-zinc-900 text-white border-black",
  "Low Fit": "bg-zinc-100 text-zinc-500 border-zinc-300",
  Enriching: "bg-amber-50 text-amber-700 border-amber-200",
  Failed: "bg-red-50 text-red-700 border-red-200",
};

export default function Prospects() {
  const { user } = useAuth();
  if (user.role === "leadgen") return <LeadGenQueue />;
  return <AdminProspects />;
}

/* ---------------- Lead-Gen daily queue ---------------- */
function LeadGenQueue() {
  const [tab, setTab] = useState("New");
  const [items, setItems] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [search, setSearch] = useState("");
  const [tier, setTier] = useState("");
  const [sort, setSort] = useState("score");
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ total: 0, pages: 1, limit: 20 });
  const [stats, setStats] = useState(null);
  const [pps, setPps] = useState([]);
  const [activePP, setActivePP] = useState("");
  const [ppStats, setPpStats] = useState([]);

  const loadPps = useCallback(() => api.get("/pseudo-profiles").then((r) => {
    setPps(r.data);
    setActivePP((cur) => cur || (r.data[0]?.id || ""));
  }).catch(() => {}), []);
  const loadPpStats = useCallback(() => api.get("/analytics/pseudo-profiles").then((r) => setPpStats(r.data)).catch(() => {}), []);
  useEffect(() => { loadPps(); loadPpStats(); }, [loadPps, loadPpStats]);

  const addPP = async () => {
    const name = window.prompt("Pseudo profile name (the LinkedIn account you send from):");
    if (!name) return;
    try { const { data } = await api.post("/pseudo-profiles", { name }); toast.success("Pseudo profile added"); setActivePP(data.id); loadPps(); }
    catch (e) { toast.error(errText(e)); }
  };
  const delPP = async () => {
    if (!activePP) return;
    if (!window.confirm("Remove this pseudo profile?")) return;
    try { await api.delete(`/pseudo-profiles/${activePP}`); setActivePP(""); loadPps(); toast.success("Removed"); }
    catch (e) { toast.error(errText(e)); }
  };

  const load = useCallback(() => {
    const params = { status: tab, sort, page, limit: 20 };
    if (search) params.search = search;
    if (tier) params.tier = tier;
    api.get("/prospects", { params }).then((r) => {
      setItems(r.data.items);
      setMeta({ total: r.data.total, pages: r.data.pages, limit: r.data.limit });
    }).catch(() => {});
  }, [tab, search, tier, sort, page]);
  const loadStats = useCallback(() => api.get("/prospects/stats").then((r) => setStats(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { setPage(1); }, [tab, search, tier, sort]);

  const copyAndSend = async (p) => {
    setBusyId(p.id);
    const ok = await copyText(p.linkedin_url);
    try {
      await api.post(`/prospects/${p.id}/sent`, { pseudo_profile_id: activePP || null });
      toast.success(ok ? "URL copied · moved to Connection Sent" : "Marked sent (copy failed — copy manually)");
      load(); loadStats(); loadPpStats();
    } catch (e) { toast.error(errText(e)); } finally { setBusyId(null); }
  };

  const mine = (stats?.per_rep || [])[0]?.counts || {};
  const connected = (mine["Connection Sent"] || 0) + (mine["Converted"] || 0);

  return (
    <div className="p-6 max-w-5xl">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-head text-2xl font-black tracking-tight">Prospecting Queue</h1>
          <p className="text-xs text-zinc-500 font-mono mb-5">Your daily list · copy the URL, send the connection request, then convert once you have their number</p>
        </div>
        <div className="flex gap-2" data-testid="leadgen-my-stats">
          <div className="bg-purple-50 border border-purple-200 rounded-xl px-4 py-2 text-center">
            <div className="font-head text-2xl font-black text-purple-700 tabular-nums">{connected}</div>
            <div className="text-[10px] font-bold uppercase tracking-wide text-purple-600">Connections sent</div>
          </div>
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-2 text-center">
            <div className="font-head text-2xl font-black text-emerald-700 tabular-nums">{mine["Converted"] || 0}</div>
            <div className="text-[10px] font-bold uppercase tracking-wide text-emerald-600">Converted</div>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="flex gap-1 bg-zinc-100 rounded-xl p-1 w-fit">
          {[["New", "To contact"], ["Connection Sent", "Connection sent"]].map(([v, l]) => (
            <button key={v} data-testid={`queue-tab-${v.replace(/\s/g, "-").toLowerCase()}`} onClick={() => setTab(v)}
              className={`px-4 py-1.5 text-xs font-bold uppercase tracking-wide rounded-lg transition-all ${tab === v ? "bg-white text-klein shadow-sm" : "text-zinc-500 hover:text-zinc-900"}`}>
              {l}
            </button>
          ))}
        </div>
        <div className="relative">
          <Search className="h-4 w-4 absolute left-3 top-2.5 text-zinc-400" />
          <input data-testid="queue-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name / company / URL"
            className="pl-9 pr-3 py-2 border border-zinc-300 rounded-lg text-sm w-60 focus:border-klein focus:outline-none" />
        </div>
        <select data-testid="queue-tier" value={tier} onChange={(e) => setTier(e.target.value)} className="border border-zinc-300 rounded-lg text-sm px-2 py-2 focus:border-klein focus:outline-none">
          <option value="">All tiers</option>
          {["Award-Signal", "Momentum Builder", "Established Operator", "Low Fit"].map((t) => <option key={t}>{t}</option>)}
        </select>
        <select data-testid="queue-sort" value={sort} onChange={(e) => setSort(e.target.value)} className="border border-zinc-300 rounded-lg text-sm px-2 py-2 focus:border-klein focus:outline-none">
          <option value="score">Sort: score</option>
          <option value="recent">Sort: recent</option>
          <option value="name">Sort: name</option>
        </select>
        <span className="text-xs text-zinc-400 font-mono ml-auto">{meta.total} total</span>
      </div>

      {/* Pseudo profile selector */}
      <div className="flex items-center gap-2 mb-4 bg-purple-50 border border-purple-200 rounded-xl px-3 py-2" data-testid="pseudo-profile-bar">
        <span className="text-xs font-bold uppercase tracking-wide text-purple-700 shrink-0">Sending as</span>
        <select data-testid="pseudo-profile-select" value={activePP} onChange={(e) => setActivePP(e.target.value)}
          className="border border-purple-300 rounded-lg text-sm px-2 py-1.5 bg-white focus:border-klein focus:outline-none min-w-[180px]">
          <option value="">— no profile —</option>
          {pps.map((pp) => <option key={pp.id} value={pp.id}>{pp.name}</option>)}
        </select>
        <button data-testid="add-pseudo-profile" onClick={addPP} className="text-xs font-medium text-klein hover:underline">+ Add profile</button>
        {activePP && <button data-testid="del-pseudo-profile" onClick={delPP} className="text-zinc-400 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>}
        <span className="text-[11px] text-purple-600/80 ml-auto hidden sm:block">This account gets attached to every prospect you send from here</span>
      </div>

      {ppStats.length > 0 && (
        <div className="bg-white border border-zinc-200 rounded-xl p-3 mb-4" data-testid="pseudo-analytics">
          <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-2">Which profile performs best</div>
          <div className="flex flex-wrap gap-2">
            {ppStats.map((s) => (
              <div key={s.name} data-testid={`pp-stat-${s.name}`} className="flex items-center gap-2 border border-zinc-200 rounded-lg px-2.5 py-1.5">
                <span className="text-xs font-medium">{s.name}</span>
                <span className="text-[11px] font-mono text-emerald-700 font-bold">{s.leads} leads</span>
                <span className="text-[11px] font-mono text-purple-600">{s.sent} sent</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-2.5" data-testid="leadgen-queue">
        {items.map((p) => (
          <div key={p.id} data-testid={`prospect-${p.id}`} className="bg-white border border-zinc-200 rounded-xl p-4 flex items-center gap-4 rise">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-sm truncate">{p.name || "Unknown"}</span>
                <span className="font-mono text-sm font-bold text-klein tabular-nums">{p.fit_score}</span>
                <TierBadge tier={p.tier} />
              </div>
              <div className="text-xs text-zinc-500 truncate">{p.headline || p.company}</div>
              <a href={p.linkedin_url} target="_blank" rel="noreferrer" data-testid={`prospect-link-${p.id}`}
                className="inline-flex items-center gap-1 text-[11px] text-klein hover:underline mt-1 font-mono truncate max-w-full">
                <Linkedin className="h-3 w-3" /> {p.linkedin_url}
              </a>
              {p.pseudo_profile_name && (
                <div className="text-[10px] text-purple-600 font-mono mt-0.5" data-testid={`prospect-pp-${p.id}`}>via {p.pseudo_profile_name}</div>
              )}
            </div>
            {tab === "New" ? (
              <button data-testid={`copy-send-${p.id}`} onClick={() => copyAndSend(p)} disabled={busyId === p.id}
                className="shrink-0 flex items-center gap-2 bg-klein hover:bg-klein-hover text-white text-xs font-bold uppercase tracking-wide px-3.5 py-2.5 rounded-lg transition-colors disabled:opacity-60">
                {busyId === p.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Copy className="h-4 w-4" />} Copy + Mark Sent
              </button>
            ) : (
              <ConvertButton prospect={p} onDone={load} />
            )}
          </div>
        ))}
        {items.length === 0 && (
          <div className="text-center text-zinc-400 font-mono py-16 text-xs border border-dashed border-zinc-300 rounded-xl">
            {tab === "New" ? "Queue is empty — ask your admin/BDE to add more profiles." : "No pending connections."}
          </div>
        )}
      </div>
      <Pagination page={page} pages={meta.pages} total={meta.total} limit={meta.limit} onPage={setPage} />
    </div>
  );
}

function ConvertButton({ prospect, onDone }) {
  const { openLead } = useDrawer();
  const [open, setOpen] = useState(false);
  const [phone, setPhone] = useState(prospect.phone || "");
  const [email, setEmail] = useState(prospect.email || "");
  const [busy, setBusy] = useState(false);

  const convert = async () => {
    if (!phone.trim()) { toast.error("Enter their phone number"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/prospects/${prospect.id}/convert`, { phone, email });
      toast.success(`Converted → sent to ${data.assigned_to_name || "BDE pipeline"}`);
      setOpen(false); onDone();
      if (data.lead?.id) openLead(data.lead.id);
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };

  if (!open) return (
    <button data-testid={`convert-open-${prospect.id}`} onClick={() => setOpen(true)}
      className="shrink-0 flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold uppercase tracking-wide px-3.5 py-2.5 rounded-lg transition-colors">
      <Phone className="h-4 w-4" /> Got number → Convert
    </button>
  );
  return (
    <div className="shrink-0 flex items-center gap-1.5">
      <input data-testid={`convert-phone-${prospect.id}`} value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone *"
        className="border border-zinc-300 rounded-lg text-xs px-2 py-2 w-28 focus:border-klein focus:outline-none" />
      <input data-testid={`convert-email-${prospect.id}`} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email"
        className="border border-zinc-300 rounded-lg text-xs px-2 py-2 w-32 focus:border-klein focus:outline-none" />
      <button data-testid={`convert-submit-${prospect.id}`} onClick={convert} disabled={busy}
        className="bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg p-2 transition-colors disabled:opacity-60">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
      </button>
    </div>
  );
}

/* ---------------- Admin / BDE view ---------------- */
function AdminProspects() {
  const { user } = useAuth();
  const [urls, setUrls] = useState("");
  const [reps, setReps] = useState([]);
  const [leadgen, setLeadgen] = useState("");
  const [importing, setImporting] = useState(false);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState("");
  const [items, setItems] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [leadStats, setLeadStats] = useState([]);
  const [ppStats, setPpStats] = useState([]);
  const [repFilter, setRepFilter] = useState("");
  const [tierFilter, setTierFilter] = useState("");
  const [searchQ, setSearchQ] = useState("");
  const [sortBy, setSortBy] = useState("score");
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ total: 0, pages: 1, limit: 25 });
  const fileRef = useRef();
  const seenJobs = useRef({});

  const loadStats = useCallback(() => api.get("/prospects/stats").then((r) => setStats(r.data)).catch(() => {}), []);
  const loadLeadStats = useCallback(() => api.get("/prospects/leadgen-stats").then((r) => setLeadStats(r.data)).catch(() => {}), []);
  const loadPpStats = useCallback(() => api.get("/analytics/pseudo-profiles").then((r) => setPpStats(r.data)).catch(() => {}), []);
  const loadItems = useCallback(() => {
    const params = { sort: sortBy, page, limit: 25 };
    if (filter) params.status = filter;
    if (repFilter) params.assigned_leadgen = repFilter;
    if (tierFilter) params.tier = tierFilter;
    if (searchQ) params.search = searchQ;
    api.get("/prospects", { params }).then((r) => {
      setItems(r.data.items);
      setMeta({ total: r.data.total, pages: r.data.pages, limit: r.data.limit });
    }).catch(() => {});
  }, [filter, repFilter, tierFilter, searchQ, sortBy, page]);
  const loadJobs = useCallback(() => {
    api.get("/prospects/jobs").then((r) => {
      const js = r.data || [];
      js.forEach((j) => {
        const prev = seenJobs.current[j.id];
        if (prev && prev !== "done" && j.status === "done") {
          const ok = j.total - j.failed;
          toast.success(`Enrichment complete · ${ok} added${j.failed ? `, ${j.failed} failed` : ""}`);
        }
        seenJobs.current[j.id] = j.status;
      });
      setJobs(js);
    }).catch(() => {});
  }, []);

  useEffect(() => { api.get("/team/leadgens").then((r) => setReps(r.data)).catch(() => {}); }, []);
  useEffect(() => { setPage(1); }, [filter, repFilter, tierFilter, searchQ, sortBy]);
  useEffect(() => { loadStats(); loadItems(); loadJobs(); loadLeadStats(); loadPpStats(); }, [loadStats, loadItems, loadJobs, loadLeadStats, loadPpStats]);
  useEffect(() => {
    const t = setInterval(() => { loadStats(); loadItems(); loadJobs(); loadLeadStats(); loadPpStats(); }, 5000);
    return () => clearInterval(t);
  }, [loadStats, loadItems, loadJobs, loadLeadStats, loadPpStats]);

  const doImport = async () => {
    const list = urls.split(/[\n,]/).map((u) => u.trim()).filter(Boolean);
    if (list.length === 0) { toast.error("Paste at least one LinkedIn URL"); return; }
    if (!leadgen) { toast.error("Assign a lead-gen rep"); return; }
    setImporting(true);
    try {
      const { data } = await api.post("/prospects/import", { urls: list, assigned_leadgen: leadgen });
      toast.success(`Queued ${data.queued} profile(s) for enrichment${data.skipped ? ` · ${data.skipped} skipped` : ""}`);
      setUrls(""); loadStats(); loadItems(); loadJobs();
    } catch (e) { toast.error(errText(e)); } finally { setImporting(false); }
  };

  const uploadCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!leadgen) { toast.error("Pick a lead-gen rep first"); e.target.value = ""; return; }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("assigned_leadgen", leadgen);
    try {
      const { data } = await api.post("/prospects/import-csv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Found & queued ${data.queued} URL(s) from ${file.name}${data.skipped ? ` · ${data.skipped} skipped` : ""}`);
      loadStats(); loadItems(); loadJobs();
    } catch (e2) { toast.error(errText(e2)); } finally { e.target.value = ""; }
  };

  const del = async (p) => {
    try { await api.delete(`/prospects/${p.id}`); loadStats(); loadItems(); toast.success("Deleted"); }
    catch (e) { toast.error(errText(e)); }
  };

  const totals = stats?.totals || {};
  const STATUSES = ["", "New", "Connection Sent", "Enriching", "Low Fit", "Converted", "Failed"];
  const activeJobs = jobs.filter((j) => j.status === "running");
  const lowReps = (stats?.per_rep || []).filter((r) => r.leadgen_id && r.remaining < 5);

  return (
    <div className="p-6 max-w-6xl">
      <h1 className="font-head text-2xl font-black tracking-tight">Prospecting</h1>
      <p className="text-xs text-zinc-500 font-mono mb-5">Upload LinkedIn URLs → auto-enrich & ICP-score → high-fit prospects surface to your lead-gen reps</p>

      {/* Low-queue alert */}
      {lowReps.length > 0 && (
        <div className="mb-4 border-2 border-red-300 bg-red-50 rounded-xl px-4 py-3 flex items-start gap-3" data-testid="low-queue-alert">
          <AlertCircle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
          <div className="text-sm text-red-800">
            <span className="font-bold">Top up needed —</span> {lowReps.map((r) => `${r.leadgen_name} (${r.remaining} left)`).join(", ")}. Add more profiles so no rep runs dry.
          </div>
        </div>
      )}

      {/* Live enrichment progress */}
      {activeJobs.map((j) => (
        <div key={j.id} className="mb-4 border-2 border-klein/40 bg-blue-50 rounded-xl px-4 py-3" data-testid={`enrich-progress-${j.id}`}>
          <div className="flex items-center gap-2 text-sm text-klein font-medium">
            <Loader2 className="h-4 w-4 animate-spin" /> Enriching {j.done} of {j.total} profiles{j.assigned_leadgen_name ? ` for ${j.assigned_leadgen_name}` : ""}…
          </div>
          <div className="h-2 bg-blue-200 rounded-full mt-2 overflow-hidden">
            <div className="h-2 bg-klein transition-all" style={{ width: `${j.total ? Math.round((j.done / j.total) * 100) : 0}%` }} />
          </div>
        </div>
      ))}

      <div className="grid lg:grid-cols-2 gap-5 mb-6">
        {/* Import */}
        <div className="bg-white border-2 border-black rounded-xl p-4" data-testid="import-panel">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider mb-3"><Upload className="h-4 w-4" /> Add profiles to a rep's queue</div>
          <select data-testid="assign-leadgen" value={leadgen} onChange={(e) => setLeadgen(e.target.value)}
            className="w-full border border-zinc-300 rounded-lg text-sm px-2 py-2 mb-2 focus:border-klein focus:outline-none">
            <option value="">Assign to lead-gen rep…</option>
            {reps.map((r) => <option key={r.id} value={r.id}>{r.name}{r.assigned_bde ? "" : " (no BDE linked)"}</option>)}
          </select>
          <textarea data-testid="prospect-urls" value={urls} onChange={(e) => setUrls(e.target.value)} rows={4}
            placeholder="Paste LinkedIn profile URLs — one per line&#10;https://www.linkedin.com/in/username"
            className="w-full border border-zinc-300 rounded-lg p-2.5 text-sm font-mono resize-none focus:border-klein focus:outline-none" />
          <div className="flex items-center gap-2 mt-2">
            <button data-testid="import-prospects-btn" onClick={doImport} disabled={importing}
              className="flex-1 bg-klein hover:bg-klein-hover text-white text-sm font-medium px-4 py-2 rounded-lg flex items-center justify-center gap-2 transition-colors disabled:opacity-60">
              {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Enrich & Queue
            </button>
            <input ref={fileRef} type="file" accept=".csv,.txt,.tsv" onChange={uploadCsv} className="hidden" data-testid="csv-file-input" />
            <button data-testid="upload-csv-btn" onClick={() => fileRef.current?.click()}
              className="flex items-center gap-1.5 border border-zinc-300 rounded-lg text-sm font-medium px-3 py-2 hover:border-klein hover:text-klein transition-colors">
              <FileUp className="h-4 w-4" /> Upload CSV
            </button>
          </div>
          <p className="text-[11px] text-zinc-400 mt-2">Paste URLs or upload a CSV / Apify export — we pull every LinkedIn URL automatically. Only prospects scoring ≥ {stats?.threshold ?? 60} reach the rep's queue.</p>
        </div>

        {/* Remaining per rep */}
        <div className="bg-white border border-zinc-300 rounded-xl p-4" data-testid="remaining-panel">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider mb-3"><Users2 className="h-4 w-4" /> Remaining in each rep's queue</div>
          <div className="space-y-2">
            {(stats?.per_rep || []).map((r) => (
              <div key={r.leadgen_id || "none"} className="flex items-center justify-between border-b border-zinc-100 pb-2 last:border-0" data-testid={`rep-remaining-${r.leadgen_id || "none"}`}>
                <div className="flex items-center gap-2 text-sm"><UserCheck className="h-4 w-4 text-zinc-400" /> {r.leadgen_name}</div>
                <div className="flex items-center gap-3 text-xs font-mono">
                  <span className="text-emerald-700 font-bold">{r.counts?.New || 0} to contact</span>
                  <span className="text-klein">{r.counts?.["Connection Sent"] || 0} sent</span>
                  <span className={`font-bold ${r.leadgen_id && r.remaining < 5 ? "text-red-600" : "text-zinc-700"}`}>{r.remaining} left</span>
                </div>
              </div>
            ))}
            {(stats?.per_rep || []).length === 0 && <div className="text-xs text-zinc-400 font-mono py-4 text-center">No prospects yet.</div>}
          </div>
        </div>
      </div>

      {/* Leads generated by each lead-gen rep */}
      <div className="bg-white border border-zinc-300 rounded-xl p-4 mb-5" data-testid="leadgen-generated-card">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider mb-3"><TrendingUp className="h-4 w-4" /> Activity by rep</div>
        <div className="grid sm:grid-cols-2 gap-2">
          {leadStats.map((r) => (
            <div key={r.leadgen_id} className="flex items-center justify-between border border-zinc-200 rounded-lg px-3 py-2" data-testid={`leadgen-generated-${r.leadgen_id}`}>
              <div className="flex items-center gap-2 text-sm font-medium min-w-0"><UserCheck className="h-4 w-4 text-zinc-400 shrink-0" /><span className="truncate">{r.leadgen_name}</span></div>
              <div className="flex items-center gap-3 text-xs font-mono shrink-0">
                <span className="flex items-center gap-1 text-purple-600" title="connection requests sent"><Link2 className="h-3 w-3" />{r.sent_total} connected</span>
                <span className="text-emerald-700 font-bold" title="leads generated today">{r.today} today</span>
                <span className="text-zinc-500" title="leads generated all time">{r.total} leads</span>
              </div>
            </div>
          ))}
          {leadStats.length === 0 && <div className="text-xs text-zinc-400 font-mono py-2">No lead-gen reps yet.</div>}
        </div>
      </div>

      {/* Weekly leaderboard */}
      {leadStats.some((r) => r.week > 0 || r.sent_week > 0) && (
        <div className="bg-gradient-to-br from-amber-50 to-white border-2 border-gold rounded-xl p-4 mb-5" data-testid="leaderboard-card">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider mb-3"><Crown className="h-4 w-4 text-gold" /> This week's leaderboard</div>
          <div className="space-y-1.5">
            {[...leadStats].sort((a, b) => (b.week - a.week) || (b.sent_week - a.sent_week)).map((r, i) => (
              <div key={r.leadgen_id} data-testid={`leaderboard-row-${r.leadgen_id}`}
                className={`flex items-center gap-3 rounded-lg px-3 py-2 ${i === 0 ? "bg-gold/15 border border-gold" : "bg-white border border-zinc-200"}`}>
                <div className="w-6 text-center font-mono font-black">
                  {i === 0 ? <Crown className="h-4 w-4 text-gold mx-auto" /> : i === 1 ? <Medal className="h-4 w-4 text-zinc-400 mx-auto" /> : i === 2 ? <Medal className="h-4 w-4 text-amber-700 mx-auto" /> : i + 1}
                </div>
                <div className="flex-1 font-medium text-sm truncate">{r.leadgen_name}</div>
                <div className="font-mono text-xs text-purple-600">{r.sent_week} sent</div>
                <div className="font-head text-lg font-black text-klein tabular-nums w-16 text-right">{r.week}<span className="text-[10px] text-zinc-400 font-mono font-normal"> leads</span></div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Totals + filters */}
      {ppStats.length > 0 && (
        <div className="bg-white border border-zinc-300 rounded-xl p-4 mb-5" data-testid="admin-pseudo-analytics">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider mb-3"><Users2 className="h-4 w-4" /> Pseudo profile performance</div>
          <div className="flex flex-wrap gap-2">
            {ppStats.map((s) => (
              <div key={s.name} data-testid={`admin-pp-stat-${s.name}`} className="flex items-center gap-2 border border-zinc-200 rounded-lg px-3 py-1.5">
                <span className="text-sm font-medium">{s.name}</span>
                <span className="text-xs font-mono text-emerald-700 font-bold">{s.leads} leads</span>
                <span className="text-xs font-mono text-purple-600">{s.sent} sent</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap mb-3">
        {["New", "Connection Sent", "Enriching", "Low Fit", "Converted", "Failed"].map((s) => (
          <span key={s} className={`text-[11px] font-mono px-2 py-1 rounded-md border ${STATUS_STYLE[s]}`}>{s}: {totals[s] || 0}</span>
        ))}
      </div>
      <div className="flex items-center gap-2 flex-wrap mb-3">
        <div className="relative">
          <Search className="h-4 w-4 absolute left-3 top-2.5 text-zinc-400" />
          <input data-testid="admin-prospect-search" value={searchQ} onChange={(e) => setSearchQ(e.target.value)} placeholder="Search name / company / URL"
            className="pl-9 pr-3 py-2 border border-zinc-300 rounded-lg text-sm w-56 focus:border-klein focus:outline-none" />
        </div>
        <select data-testid="admin-rep-filter" value={repFilter} onChange={(e) => setRepFilter(e.target.value)} className="border border-zinc-300 rounded-lg text-sm px-2 py-1.5 focus:border-klein focus:outline-none">
          <option value="">All reps</option>
          {reps.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
        </select>
        <select data-testid="admin-tier-filter" value={tierFilter} onChange={(e) => setTierFilter(e.target.value)} className="border border-zinc-300 rounded-lg text-sm px-2 py-1.5 focus:border-klein focus:outline-none">
          <option value="">All tiers</option>
          {["Award-Signal", "Momentum Builder", "Established Operator", "Low Fit"].map((t) => <option key={t}>{t}</option>)}
        </select>
        <select data-testid="prospect-filter" value={filter} onChange={(e) => setFilter(e.target.value)}
          className="border border-zinc-300 rounded-lg text-sm px-2 py-1.5 focus:border-klein focus:outline-none">
          {STATUSES.map((s) => <option key={s} value={s}>{s || "All statuses"}</option>)}
        </select>
        <select data-testid="admin-sort" value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="border border-zinc-300 rounded-lg text-sm px-2 py-1.5 focus:border-klein focus:outline-none">
          <option value="score">Sort: score</option>
          <option value="recent">Sort: recent</option>
          <option value="name">Sort: name</option>
        </select>
        <span className="text-xs text-zinc-400 font-mono ml-auto">{meta.total} total</span>
      </div>

      {/* List */}
      <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden" data-testid="admin-prospect-list">
        {items.map((p) => (
          <div key={p.id} className="flex items-center gap-3 px-4 py-2.5 border-b border-zinc-100 last:border-0" data-testid={`admin-prospect-${p.id}`}>
            <div className="w-10 text-center font-mono font-bold tabular-nums">{p.enrichment_status === "pending" ? <Loader2 className="h-4 w-4 animate-spin mx-auto text-amber-500" /> : p.fit_score}</div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium truncate">{p.name || <span className="text-zinc-400">Enriching…</span>}</div>
              <div className="text-[11px] text-zinc-500 truncate font-mono">{p.linkedin_url}</div>
            </div>
            <span className="text-[11px] text-zinc-500 truncate max-w-[120px]">{p.assigned_leadgen_name || "—"}</span>
            {p.status === "Failed" && <AlertTriangle className="h-4 w-4 text-red-500" title="Enrichment failed" />}
            {p.status === "Enriching" && <Clock className="h-4 w-4 text-amber-500" />}
            <span className={`text-[10px] font-mono uppercase px-2 py-0.5 rounded-md border ${STATUS_STYLE[p.status] || ""}`}>{p.status}</span>
            {user.role === "admin" && (
              <button data-testid={`delete-prospect-${p.id}`} onClick={() => del(p)} className="text-zinc-400 hover:text-red-600 transition-colors"><Trash2 className="h-4 w-4" /></button>
            )}
          </div>
        ))}
        {items.length === 0 && <div className="text-center text-zinc-400 font-mono py-12 text-xs">No prospects{filter ? ` with status "${filter}"` : ""} yet.</div>}
      </div>
      <Pagination page={page} pages={meta.pages} total={meta.total} limit={meta.limit} onPage={setPage} />
    </div>
  );
}
